import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { Star, Paperclip, AlertCircle, Clock, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { MessageListItem } from '@/lib/api/endpoints';

const CATEGORY_LABEL: Record<string, string> = {
  preventivo: 'Preventivo',
  fattura: 'Fattura',
  supporto: 'Supporto',
  fyi: 'FYI',
  spam: 'Spam',
  personale: 'Personale',
  altro: 'Altro',
};

const URGENCY_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'warning'
> = {
  low: 'secondary',
  medium: 'default',
  high: 'warning',
  critical: 'destructive',
};

export interface MessageListProps {
  messages: MessageListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
}

export function MessageList({ messages, selectedId, onSelect, loading }: MessageListProps) {
  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center px-6">
          <div className="text-sm">Nessun messaggio in questa vista</div>
          <div className="text-xs mt-1 opacity-70">Tutto pulito qui.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {messages.map((m, idx) => {
        const fromName = m.from_address?.name || m.from_address?.email || 'Sconosciuto';
        const dateStr = m.received_at
          ? formatDistanceToNow(new Date(m.received_at), { locale: it, addSuffix: false })
          : '';
        const isStarred = m.is_starred || m.thread_starred;

        return (
          <motion.button
            key={m.id}
            type="button"
            onClick={() => onSelect(m.id)}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15, delay: Math.min(idx * 0.01, 0.1) }}
            className={cn(
              'w-full text-left px-4 py-3 border-b transition-colors',
              'hover:bg-muted/50',
              selectedId === m.id && 'bg-primary/5 border-l-2 border-l-primary',
              !m.is_read && 'bg-background',
              m.is_read && 'bg-muted/20'
            )}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 pt-1.5">
                <div
                  className={cn(
                    'w-2 h-2 rounded-full',
                    !m.is_read ? 'bg-primary' : 'bg-transparent'
                  )}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={cn('text-sm truncate', !m.is_read && 'font-semibold')}>
                    {fromName}
                  </span>
                  {m.message_count > 1 && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      ({m.message_count})
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
                    {dateStr}
                  </span>
                </div>

                <div className={cn('text-sm truncate mb-1', !m.is_read && 'font-medium')}>
                  {m.subject || '(Nessun oggetto)'}
                </div>

                <div className="text-xs text-muted-foreground truncate mb-2">
                  {m.snippet}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  {m.ai_category && (
                    <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                      {CATEGORY_LABEL[m.ai_category] ?? m.ai_category}
                    </Badge>
                  )}
                  {m.ai_urgency && m.ai_urgency !== 'low' && (
                    <Badge
                      variant={URGENCY_VARIANT[m.ai_urgency] ?? 'default'}
                      className="text-[10px] py-0 px-1.5"
                    >
                      {m.ai_urgency === 'critical' && (
                        <AlertCircle className="h-3 w-3 mr-0.5" />
                      )}
                      {m.ai_urgency}
                    </Badge>
                  )}
                  {m.snoozed_until && (
                    <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                      <Clock className="h-3 w-3 mr-0.5" /> Snooze
                    </Badge>
                  )}
                  {m.has_attachments && (
                    <Paperclip className="h-3 w-3 text-muted-foreground" />
                  )}
                  {isStarred && (
                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                  )}
                </div>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
}
